// SHADOW MARKET™ — Pattern detail page inside the Academy.
// Route: /academy/patrones/:id
import React, { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Target,
  Lightbulb,
  ListChecks,
  TrendingUp,
  TrendingDown,
  Minus,
  Layers,
  GraduationCap,
} from "lucide-react";
import { ASSETS } from "../assets";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { SimulatorRankCard } from "../components/SimulatorRankCard";
import { PatronDiagrama } from "../components/academy/PatronDiagrama";
import { PATRONES } from "../data/patronesGraficos";
import { useLanguage } from "../i18n/LanguageContext";
import { getPatronText, getPatronesUi } from "../i18n/patrones";

const dirCfg = {
  alcista: {
    cls: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
    Icon: TrendingUp,
  },
  bajista: {
    cls: "border-red-400/30 bg-red-400/10 text-red-300",
    Icon: TrendingDown,
  },
  neutro: {
    cls: "border-amber-400/30 bg-amber-400/10 text-amber-300",
    Icon: Minus,
  },
};
const tipoCfg = {
  continuacion: "border-cyan-400/30 bg-cyan-400/10 text-cyan-300",
  reversion: "border-fuchsia-400/30 bg-fuchsia-400/10 text-fuchsia-300",
  neutro: "border-amber-400/30 bg-amber-400/10 text-amber-300",
};
const nivelCfg = {
  basico: "border-white/15 bg-white/[0.05] text-white/60",
  intermedio: "border-orange-400/40 bg-orange-400/10 text-orange-300",
};

const AcademyPatronDetailPage = () => {
  const { id } = useParams();
  const { lang } = useLanguage();
  const ui = getPatronesUi(lang);
  const { patron, idx } = useMemo(() => {
    const i = PATRONES.findIndex((p) => p.id === id);
    return { patron: i >= 0 ? PATRONES[i] : null, idx: i };
  }, [id]);

  const txt = patron ? getPatronText(patron.id, lang) || {} : {};
  const txtPrev = idx > 0 ? getPatronText(PATRONES[idx - 1].id, lang) || {} : {};
  const txtNext =
    idx < PATRONES.length - 1
      ? getPatronText(PATRONES[idx + 1].id, lang) || {}
      : {};

  if (!patron) return <Navigate to="/academy/patrones" replace />;

  const prev = idx > 0 ? PATRONES[idx - 1] : null;
  const next = idx < PATRONES.length - 1 ? PATRONES[idx + 1] : null;
  const dir = dirCfg[patron.direccion];

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black text-white">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(255,140,0,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_50%,rgba(168,85,247,0.10),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_70%,rgba(0,229,255,0.10),transparent_55%)]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
      </div>

      {/* HEADER */}
      <header className="relative z-20 border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-10 md:py-4">
          <Link
            to="/academy/patrones"
            className="group inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white"
            data-testid="patron-detail-back"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">
              {ui.back_all}
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <img src={ASSETS.logos.rebel} alt="REBEL" className="h-8 w-auto md:h-10" />
          </div>
          <div className="flex items-center gap-3">
            <SimulatorRankCard />
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-4 py-8 md:px-10">
        {/* Breadcrumb */}
        <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
          <Link to="/academy" className="transition-colors hover:text-orange-300">
            {ui.breadcrumb_academy}
          </Link>
          <span className="px-2 text-white/25">→</span>
          <Link
            to="/academy/patrones"
            className="transition-colors hover:text-orange-300"
          >
            {ui.breadcrumb_patterns}
          </Link>
          <span className="px-2 text-white/25">→</span>
          <span className="text-orange-300">{txt.nombre || patron.nombre}</span>
        </p>

        {/* Title + badges */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="mb-6"
        >
          <h1
            className="text-3xl font-black tracking-tight text-white md:text-5xl"
            data-testid="patron-detail-title"
          >
            {txt.nombre || patron.nombre}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] ${tipoCfg[patron.tipo]}`}
            >
              {ui.tipo_badge[patron.tipo]}
            </span>
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] ${dir.cls}`}
            >
              <dir.Icon className="h-3 w-3" />
              {ui.direccion[patron.direccion]}
            </span>
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] ${nivelCfg[patron.nivel]}`}
            >
              {ui.nivel[patron.nivel]}
            </span>
          </div>
        </motion.div>

        {/* Diagram — same glass card style as Academy lessons */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl"
        >
          <div className="bg-[#07080f] p-6 md:p-10">
            <PatronDiagrama id={patron.id} />
          </div>
        </motion.div>

        {/* Description */}
        <section className="mb-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl">
          <h2 className="mb-3 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-orange-300">
            <Layers className="h-3.5 w-3.5" />
            {ui.sec_description}
          </h2>
          <p className="text-sm leading-relaxed text-white/75 md:text-base">
            {txt.descripcion || patron.descripcion}
          </p>
        </section>

        {/* Structure list */}
        <section className="mb-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl">
          <h2 className="mb-4 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-orange-300">
            <ListChecks className="h-3.5 w-3.5" />
            {ui.sec_structure}
          </h2>
          <ol className="space-y-2.5">
            {(txt.estructura || patron.estructura).map((step, i) => (
              <li
                key={i}
                className="flex items-start gap-3"
                data-testid={`patron-step-${i}`}
              >
                <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-orange-400/40 bg-orange-400/10 text-[10px] font-black text-orange-300">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-sm leading-relaxed text-white/75">
                  {step}
                </span>
              </li>
            ))}
          </ol>
        </section>

        {/* Price target callout */}
        <section
          className="mb-8 overflow-hidden rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-cyan-400/[0.08] to-transparent p-6 backdrop-blur-xl"
          data-testid="patron-objetivo"
        >
          <h2 className="mb-3 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">
            <Target className="h-3.5 w-3.5" />
            {ui.sec_target}
          </h2>
          <p className="text-sm leading-relaxed text-white/85 md:text-base">
            {txt.objetivo || patron.objetivo}
          </p>
        </section>

        {/* Pro tip callout — same purple/amber treatment as Academy tips */}
        <section
          className="mb-12 overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-400/[0.08] to-transparent p-6 backdrop-blur-xl"
          data-testid="patron-consejo"
        >
          <h2 className="mb-3 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
            <Lightbulb className="h-3.5 w-3.5" />
            {ui.sec_tip}
          </h2>
          <p className="text-sm leading-relaxed text-white/85 md:text-base">
            {txt.consejo || patron.consejo}
          </p>
        </section>

        {/* Navigation footer */}
        <nav className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {prev ? (
            <Link
              to={`/academy/patrones/${prev.id}`}
              className="group flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left backdrop-blur-xl transition-all hover:border-orange-400/40 hover:text-white"
              data-testid="patron-detail-prev"
            >
              <ArrowLeft className="h-4 w-4 shrink-0 text-orange-300 transition-transform group-hover:-translate-x-1" />
              <div className="min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/40">
                  {ui.nav_prev}
                </div>
                <div className="truncate text-xs font-bold text-white">
                  {txtPrev.nombre || prev.nombre}
                </div>
              </div>
            </Link>
          ) : (
            <div />
          )}

          <Link
            to="/academy/patrones"
            className="group inline-flex items-center justify-center gap-2 rounded-xl border border-orange-400/40 bg-orange-400/[0.08] px-4 py-3 text-center text-[10px] font-black uppercase tracking-[0.22em] text-orange-300 backdrop-blur-xl transition-all hover:bg-orange-400/[0.14]"
            data-testid="patron-detail-all"
          >
            <GraduationCap className="h-4 w-4" />
            {ui.nav_all}
          </Link>

          {next ? (
            <Link
              to={`/academy/patrones/${next.id}`}
              className="group flex items-center justify-end gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-right backdrop-blur-xl transition-all hover:border-orange-400/40 hover:text-white"
              data-testid="patron-detail-next"
            >
              <div className="min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/40">
                  {ui.nav_next}
                </div>
                <div className="truncate text-xs font-bold text-white">
                  {txtNext.nombre || next.nombre}
                </div>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-orange-300 transition-transform group-hover:translate-x-1" />
            </Link>
          ) : (
            <div />
          )}
        </nav>
      </main>
    </div>
  );
};

export default AcademyPatronDetailPage;
